// Setup → AI management → Capabilities & models: the GET /api/ai/capabilities and
// PUT /api/ai/capabilities/:capability calls (apps/server/src/aiCapabilities.mjs)
// and their mapping onto the @olytic/foundation/ai-admin components' props. Free of React, so it is unit
// tested (aiCapabilitiesApi.test.ts); useAiCapabilities.ts is the hook.
import type { AiCapability, AiCapabilityAccess, AiModel, AiModelStatus } from '@olytic/foundation/ai-admin';

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** One capability the server runs, its calls in the last 30 days and its newest call (null with none). */
export interface CapabilityFigures {
  key: string;
  label: string;
  description: string;
  access: AiCapabilityAccess;
  enabled: boolean;
  locked: boolean;
  uses: number;
  lastUsedAt: string | null;
}

/** The model the server uses. */
export interface ModelFigures {
  id: string;
  name: string;
  provider: string;
  isDefault: boolean;
  status: AiModelStatus;
}

/** The body of GET /api/ai/capabilities. */
export interface CapabilitiesResponse {
  capabilities: CapabilityFigures[];
  models: ModelFigures[];
}

/** What the Capabilities & models page hands to CapabilityList and ModelList. */
export interface CapabilitiesView {
  capabilities: AiCapability[];
  models: AiModel[];
  /** No capability was used in the last 30 days: the page still lists all of them, and says so. */
  unused: boolean;
}

/** The page's note: what a switch does (MOTHER-81). */
export const CAPABILITIES_NOTE =
  'A capability that is switched off makes no calls to the model. A change takes effect within 10 seconds.';

/** The line a switched-off capability's row reads, after its description. */
export const SWITCHED_OFF_LINE = 'Switched off by an admin.';

/**
 * The API's answer as the components' props. A capability is locked only when
 * the server says so; a switched-off one's description ends with
 * SWITCHED_OFF_LINE (CapabilityList has no other place for it); a capability
 * never used has no `lastUsedAt` rather than a null one.
 */
export function capabilitiesView(body: CapabilitiesResponse): CapabilitiesView {
  return {
    capabilities: body.capabilities.map((c) => {
      const description = c.enabled ? c.description : [c.description, SWITCHED_OFF_LINE].filter(Boolean).join(' ');
      return {
        key: c.key,
        label: c.label,
        ...(description ? { description } : {}),
        access: c.access,
        enabled: c.enabled,
        locked: c.locked,
        uses: c.uses,
        ...(c.lastUsedAt ? { lastUsedAt: c.lastUsedAt } : {}),
      };
    }),
    models: body.models.map((m) => ({
      id: m.id,
      name: m.name,
      provider: m.provider,
      isDefault: m.isDefault,
      status: m.status,
    })),
    unused: body.capabilities.every((c) => c.uses === 0),
  };
}

/** The view with one capability's switch shown as `enabled`, while its save is on its way. */
export function withSwitch(body: CapabilitiesResponse, key: string, enabled: boolean): CapabilitiesResponse {
  return { ...body, capabilities: body.capabilities.map((c) => (c.key === key ? { ...c, enabled } : c)) };
}

/** The server's message from a failed response, or `fallback`. */
async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const payload = (await response.json()) as { error?: unknown };
    if (typeof payload.error === 'string') return payload.error;
  } catch {
    // Not JSON.
  }
  return fallback;
}

/** GET /api/ai/capabilities; an error carries the server's message when it sends one. */
export async function fetchAiCapabilities(fetcher: Fetch, init?: RequestInit): Promise<CapabilitiesResponse> {
  const response = await fetcher('/api/ai/capabilities', init);
  if (!response.ok) throw new Error(await errorMessage(response, 'Could not load AI capabilities.'));
  return (await response.json()) as CapabilitiesResponse;
}

/**
 * PUT /api/ai/capabilities/:capability, switching it on or off. Resolves to the
 * GET's body as saved; an error carries the server's message, or "Try again."
 */
export async function saveCapabilitySwitch(fetcher: Fetch, key: string, enabled: boolean): Promise<CapabilitiesResponse> {
  const response = await fetcher(`/api/ai/capabilities/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  if (!response.ok) throw new Error(await errorMessage(response, 'Try again.'));
  return (await response.json()) as CapabilitiesResponse;
}
